/* eslint-disable no-useless-escape */
import { CredentialResponse, GoogleLogin } from "@react-oauth/google";
import { useQueryClient } from "@tanstack/react-query";
import { CognitoUserAttribute } from "amazon-cognito-identity-js";
import posthog from "posthog-js";
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useCookies } from "react-cookie";
import { decodeToken } from "react-jwt";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";

import ErrorToast from "../../../components/ErrorToast";
import PasswordInput from "../../../components/PasswordInput";
import Spinner from "../../../components/Spinner";
import { useAuth } from "../../../context/Auth/AuthContext";
import ResumeContext from "../../../context/ResumeContext";
import { isValidEmail, isValidPassword, isValidPhoneNumber } from "../../../handlers/validationFunctions";
import { StaticValue } from "../../../seed/constants";
import { getUserRecordState } from "../../../services/auth/accountRecovery";
import { userPool } from "../../../services/auth/cognito";
import { redirectAfterSignIn } from "../../../services/auth/postAuthRedirect";
import { setPendingSignup } from "../pendingSignup";
import LinkedInLogin from "../SigninScreen/LinkedInLogin";

const staticValue = new StaticValue();
const staticValues = staticValue.getDefaultValues();

const referer = new URLSearchParams(window.location.search).get("ref");

interface GoogleTokenPayload {
	email: string;
	given_name: string;
	family_name: string;
	picture: string;
}

const EMPTY_ERRORS = {
	firstName: "",
	lastName: "",
	email: "",
	phone: "",
	password: "",
	confirm_password: "",
};

function JobSeekerRegistrationForm() {
	const { setUserData } = useAuth();
	const [showEmailOptions, setShowEmailOptions] = useState(false);

	const navigate = useNavigate();

	const [registrationData, setRegistrationData] = useState({
		firstName: "",
		lastName: "",
		email: "",
		phone: "",
		password: "",
		confirm_password: "",
	});

	const { firstName, lastName, email, phone, password, confirm_password } = registrationData;

	const [errors, setErrors] = useState(EMPTY_ERRORS);
	const [serverError, setServerError] = useState("");
	const [loading, setLoading] = useState(false);

	// Users arriving from the resume builder already told us who they are — prefill from the resume
	// in context, without clobbering anything they have typed.
	const resumeContext = useContext(ResumeContext);

	useEffect(() => {
		const contactInfo = resumeContext?.resumeData?.contactInfo;
		if (!contactInfo) return;

		setRegistrationData((prev) => ({
			...prev,
			firstName: prev.firstName || contactInfo.firstName || "",
			lastName: prev.lastName || contactInfo.lastName || "",
			email: prev.email || contactInfo.email || "",
			phone: prev.phone || contactInfo.phoneNo || "",
		}));
	}, [resumeContext]);

	function onChange(event: React.ChangeEvent<HTMLInputElement>) {
		setRegistrationData((prev) => ({ ...prev, [event.target.name]: event.target.value }));
		setErrors(EMPTY_ERRORS);
	}

	function trackSignupFailed(reason: string) {
		posthog.capture("registration_signup_failed", { reason, method: "email" });
	}

	function handleSubmit(event: React.MouseEvent<HTMLButtonElement>) {
		event.preventDefault();
		setErrors(EMPTY_ERRORS);
		setServerError("");
		setLoading(true);

		posthog.capture("registration_signup_attempted", { method: "email" });

		if (!firstName || !lastName || email === "" || password === "" || confirm_password === "") {
			setErrors({
				...EMPTY_ERRORS,
				firstName: firstName ? "" : "First name is required",
				lastName: lastName ? "" : "Last name is required",
				email: email === "" ? "Email is required" : "",
				password: password === "" ? "Password required" : "",
				confirm_password: confirm_password === "" ? "Confirm password is required" : "",
			});
			trackSignupFailed("missing_fields");
			setLoading(false);
			return;
		}

		if (!isValidEmail(email)) {
			setErrors((prevState) => ({ ...prevState, email: "Please enter a valid email address" }));
			trackSignupFailed("invalid_email");
			setLoading(false);
			return;
		}

		if (phone && !isValidPhoneNumber(phone)) {
			setErrors((prevState) => ({ ...prevState, phone: "Please enter a valid phone number, eg. +2557xxxxxxxx" }));
			trackSignupFailed("invalid_phone");
			setLoading(false);
			return;
		}

		if (!isValidPassword(password)) {
			setErrors((prevState) => {
				return {
					...prevState,
					password:
						"<ul><li>Password must have least 1 number</li><li>Password must have least 1 special character(^ $ *)</li><li>Password must have at least 1 uppercase letter</li><li>Password must have at least 1 lowercase letter</li></ul>",
				};
			});
			trackSignupFailed("invalid_password");
			setLoading(false);
			return;
		}

		if (password !== confirm_password) {
			setErrors((prevState) => ({ ...prevState, confirm_password: "Password and confirm password should match" }));
			trackSignupFailed("password_mismatch");
			setLoading(false);
			return;
		}

		const attributeList = [new CognitoUserAttribute({ Name: "email", Value: email })];

		userPool.signUp(email, password, attributeList, null, (err) => {
			if (err) {
				const cognitoError = err as { code?: string; message?: string };
				if (cognitoError.code === "UsernameExistsException") {
					setServerError("Email already exists");
					trackSignupFailed("email_exists");
				} else {
					setServerError(cognitoError.message || "We couldn't create your account. Please try again.");
					trackSignupFailed("cognito_error");
				}
			} else {
				posthog.capture("registration_account_created", { method: "email" });

				setPendingSignup({ firstName, lastName, email, phone, method: "email" });

				const goTo = referer && referer.includes("expert-help") ? "/signin?ref=" + referer : "/onboarding";
				navigate(goTo, { state: { method: "email" } });
			}
			setLoading(false);
		});
	}

	const [passwordValidationState, setPasswordValidationState] = useState({
		length: false,
		uppercase: false,
		number: false,
		specialCharacter: false,
		match: false,
	});

	useEffect(() => {
		setPasswordValidationState({
			length: password.length >= 8,
			uppercase: /[A-Z]/.test(password),
			number: /\d/.test(password),
			specialCharacter: /[!@#$%^&*()_+\-=\[\]/{};&#39;:"\\|,.<>\/?]/.test(password),
			match: password.length > 0 && password === confirm_password,
		});
	}, [password, confirm_password]);

	const [, setCookie, removeCookie] = useCookies<string>(["resume"]);
	const queryClient = useQueryClient();

	async function onGoogleSignIn(googleUser: CredentialResponse) {
		if (googleUser.credential == null) return;

		const credential = `GO::${googleUser.credential}`;

		const expireDate = new Date();
		expireDate.setDate(expireDate.getDate() + 30);
		setCookie("Authorization", credential, {
			path: "/",
			expires: expireDate,
		});

		removeCookie(staticValues.localStorageResumeId);
		localStorage.removeItem("resumeList");
		const decodedToken = decodeToken<GoogleTokenPayload>(googleUser.credential);

		const data = {
			email: decodedToken.email,
			firstName: decodedToken.given_name,
			lastName: decodedToken.family_name,
			picture: decodedToken.picture,
		};

		setUserData(data);

		if (referer && referer.includes("expert-help")) {
			setLoading(false);
			navigate("/expert-help/personal-info");
			return;
		}

		// "Continue with Google" (and One Tap) can't tell a signup from a returning user signing back
		// in, so ask the backend before putting anyone through onboarding — same check /signin makes.
		// Only a confirmed record short-circuits it: if the check itself failed, onboarding is the safe
		// guess, since it bounces anyone who has already answered on to their profile.
		setLoading(true);
		const isReturningUser = (await getUserRecordState()) === "present";

		// Logging out doesn't clear the query cache, so a stale anonymous result can outlive the
		// sign-in that just happened in this tab.
		queryClient.removeQueries({ queryKey: ["user", "account"] });

		if (isReturningUser) {
			setLoading(false);
			posthog.capture("registration_existing_account_signed_in", { method: "google" });
			redirectAfterSignIn(credential);
			return;
		}

		posthog.capture("registration_account_created", { method: "google" });

		setPendingSignup({
			firstName: decodedToken.given_name,
			lastName: decodedToken.family_name,
			email: decodedToken.email,
			method: "google",
		});

		setLoading(false);
		navigate("/onboarding", { state: { method: "google" } });
	}

	const buttonRef = useRef<HTMLDivElement>(null);

	const buttonWidth = useMemo(() => {
		if (buttonRef.current) {
			return buttonRef.current.offsetWidth;
		}
		return 0;
	}, [buttonRef.current]);

	const inputClassName =
		"border border-gray-200 h-9 text-sm placeholder:text-sm rounded px-2 py-1 placeholder:text-[#7A7A7A]";

	return (
		<>
			<div className="mb-4 grid md:grid-cols-2 gap-4">
				<LinkedInLogin />
				<div className="flex items-center justify-center" ref={buttonRef}>
					<GoogleLogin
						onSuccess={onGoogleSignIn}
						onError={() => toast.error("Login Failed")}
						size="large"
						width={`${buttonWidth}px`}
						text="continue_with"
						useOneTap
					/>
				</div>
			</div>
			<div className="mb-3 text-center">
				<p className="text-medium font-medium">OR</p>
			</div>
			{serverError !== "" && <ErrorToast error={serverError} />}{" "}
			{showEmailOptions ? (
				<form className="tr-form">
					<div className="grid grid-cols-2 gap-3 mb-4">
						<div className="flex flex-col gap-1">
							<input
								type="text"
								className={inputClassName}
								placeholder="Your first name"
								value={firstName}
								onChange={onChange}
								name="firstName"
							/>
							{errors.firstName && <p className="text-sm text-error-default">{errors.firstName}</p>}
						</div>
						<div className="flex flex-col gap-1">
							<input
								type="text"
								className={inputClassName}
								placeholder="Your last name"
								value={lastName}
								onChange={onChange}
								name="lastName"
							/>
							{errors.lastName && <p className="text-sm text-error-default">{errors.lastName}</p>}
						</div>
					</div>
					<div className="flex flex-col gap-1 mb-4">
						<input
							type="email"
							className={inputClassName}
							placeholder="Enter your email address"
							value={email}
							onChange={onChange}
							name="email"
						/>
						{errors.email && <p className="text-sm text-error-default">{errors.email}</p>}
					</div>
					<div className="flex flex-col gap-1 mb-4">
						<input
							type="tel"
							className={inputClassName}
							placeholder="Phone number (optional)"
							value={phone}
							onChange={onChange}
							name="phone"
						/>
						{errors.phone && <p className="text-sm text-error-default">{errors.phone}</p>}
					</div>
					<div className="flex flex-col gap-1 mb-4">
						<PasswordInput
							className={inputClassName}
							placeholder="Create a password"
							onChange={onChange}
							value={password}
							name="password"
						/>
						{errors.password && <p className="text-sm text-error-default">{errors.password}</p>}
					</div>
					{password.length > 0 && (
						<>
							<div className="flex flex-col gap-1 mb-4">
								<PasswordInput
									className={inputClassName}
									placeholder="Confirm Password"
									onChange={onChange}
									value={confirm_password}
									name="confirm_password"
								/>
								{errors.confirm_password && <p className="text-sm text-error-default">{errors.confirm_password}</p>}
							</div>
							<div className="flex flex-col gap-1 mb-4 bg-gray-50 py-3 rounded shadow-sm">
								<ul className="pl-8 list-disc">
									<li className={passwordValidationState.length ? "text-success-default" : "text-error-default"}>
										Password is at least 8 characters long.
									</li>
									<li className={passwordValidationState.number ? "text-success-default" : "text-error-default"}>
										Password has at least one number
									</li>
									<li
										className={passwordValidationState.specialCharacter ? "text-success-default" : "text-error-default"}
									>
										Password has at least one special character
									</li>
									<li className={passwordValidationState.uppercase ? "text-success-default" : "text-error-default"}>
										Password has at least one uppercase letter
									</li>
									<li className={passwordValidationState.match ? "text-success-default" : "text-error-default"}>
										Password and confirm password match
									</li>
								</ul>
							</div>
						</>
					)}
					<button
						type="button"
						className="w-full rounded-md bg-primary-700 text-white font-inter text-base py-2 flex items-center justify-center"
						onClick={handleSubmit}
					>
						{loading ? <Spinner /> : "Sign Up"}
					</button>
				</form>
			) : (
				<button
					className="w-full rounded-md hover:bg-gray-100 border border-gray-500 text-gray-700 font-inter text-base py-2 flex items-center justify-center"
					onClick={() => {
						posthog.capture("registration_email_form_opened");
						setShowEmailOptions(true);
					}}
				>
					Continue with Email
				</button>
			)}
			<div className="text-sm text-[#5C5C5C] mt-4">
				<span>
					Already have an account?{" "}
					<Link to="/signin" className="font-semibold text-primary-700">
						Login
					</Link>
				</span>
			</div>
		</>
	);
}

export default JobSeekerRegistrationForm;
